import { X, Euro, AlertTriangle, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PaymentModal({ 
  customer, 
  paymentAmount, 
  setPaymentAmount, 
  discountApplied, 
  setDiscountApplied,
  excludedAmount,
  setExcludedAmount,
  onSubmit, 
  onClose 
}) {
  if (!customer) return null;

  const handleClose = () => {
    setPaymentAmount("");
    setDiscountApplied("");
    setExcludedAmount("");
    onClose();
  };

  const prepaidBalance = customer.prepaid_balance || 0;
  const hasPrepaid = prepaidBalance > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-lg">Ajouter un paiement</h3>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-4">
          {/* ALERTE PRÉPAIEMENT */}
          {hasPrepaid && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-xl animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-bold text-red-700 text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    PRÉPAIEMENT DISPONIBLE
                  </p>
                  <p className="text-red-600 font-medium text-xl">
                    {prepaidBalance.toFixed(2)}€ en réserve
                  </p>
                  <p className="text-red-500 text-sm mt-1">
                    Pensez à utiliser ce solde avant de facturer !
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4 p-3 bg-slate-50 rounded-lg">
            <p className="font-medium">{customer.first_name} {customer.last_name}</p>
            <p className="text-sm text-slate-500">
              Solde fidélité actuel: {(customer.loyalty_balance || 0).toFixed(2)}€ / 50€
            </p>
            {((customer.rewards_earned || 0) - (customer.rewards_used || 0)) > 0 && (
              <p className="text-sm text-green-600 font-medium mt-1">
                🎁 {((customer.rewards_earned || 0) - (customer.rewards_used || 0))} remise(s) de 5€ disponible(s)
              </p>
            )}
          </div>
          
          <div className="mb-4">
            <Label>Montant de la facture (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 40 (avant remise)"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="mt-1"
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Montant total de la facture (AVANT remise éventuelle)</p>
          </div>
          
          <div className="mb-4">
            <Label>Remise appliquée (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 5"
              value={discountApplied}
              onChange={(e) => setDiscountApplied(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">Si tu as appliqué une remise (fidélité ou autre)</p>
          </div>
          
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Label className="text-amber-700">👗👔 Montant hors fidélité (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Ex: 150"
              value={excludedAmount}
              onChange={(e) => setExcludedAmount(e.target.value)}
              className="mt-1 border-amber-200"
            />
            <p className="text-xs text-amber-600 mt-1">Robe de mariée / Pack chemises : ne compte PAS pour la fidélité</p>
          </div>
          
          {paymentAmount && parseFloat(paymentAmount) > 0 && (
            <div className="mb-4 p-3 bg-pink-50 rounded-lg text-sm space-y-1">
              {(() => {
                const currentBalance = customer.loyalty_balance || 0;
                const amount = parseFloat(paymentAmount) || 0;
                const discount = parseFloat(discountApplied) || 0;
                const excluded = parseFloat(excludedAmount) || 0;
                const loyaltyAmount = amount - excluded; // Montant qui compte pour la fidélité
                const grossTotal = currentBalance + loyaltyAmount;
                const rewardsEarned = Math.floor(grossTotal / 50);
                const afterRewards = grossTotal - (rewardsEarned * 50);
                const finalBalance = afterRewards - discount;
                const amountToPay = amount - discount; // Montant réel à payer
                
                return (
                  <>
                    <p className="text-slate-600">
                      Facture totale: <strong>{amount.toFixed(2)}€</strong>
                    </p>
                    {excluded > 0 && (
                      <p className="text-amber-600">
                        👗👔 Hors fidélité: -{excluded.toFixed(2)}€
                      </p>
                    )}
                    <p className="text-pink-700">
                      Fidélité: {currentBalance.toFixed(2)}€ + {loyaltyAmount.toFixed(2)}€ = <strong>{grossTotal.toFixed(2)}€</strong>
                    </p>
                    {rewardsEarned > 0 && (
                      <p className="text-green-700 font-bold">
                        🎉 Seuil atteint ! +{rewardsEarned} remise(s) de 5€ débloquée(s)
                      </p>
                    )}
                    {discount > 0 && (
                      <>
                        <p className="text-orange-600">
                          Remise appliquée: -{discount.toFixed(2)}€
                        </p>
                        <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded-lg">
                          <p className="text-green-800 font-bold text-lg text-center">
                            💰 À PAYER : {amountToPay.toFixed(2)}€
                          </p>
                        </div>
                      </>
                    )}
                    <p className="text-pink-700 font-medium pt-1 border-t border-pink-200">
                      Nouvelle progression: <strong className={finalBalance < 0 ? "text-red-600" : ""}>{finalBalance.toFixed(2)}€</strong> / 50€
                    </p>
                  </>
                );
              })()}
            </div>
          )}
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button 
              onClick={onSubmit} 
              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
              className="bg-primary hover:bg-primary/90"
            >
              <Euro className="w-4 h-4 mr-1" />
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
